import React, { useEffect, useState } from "react";
import { Form, DatePicker, Typography } from "antd";
import dayjs from "../../utils/day";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const PeriodPicker = ({
  state = {},
  style,
  minDate,
  maxDate,
  allowClear = false,
  occupiedDates = [],
  maxRangeInDays,
  setWarningMessage = () => {},
}) => {
  const { data, form, rules, onChange, keyName } = state;
  const [firstPicked, setFirstPicked] = useState(null);

  const handlePeriodChange = (value) => {
    if (!value) {
      setFirstPicked(null);
      return;
    }

    let [selectedStart, selectedEnd] = value;
    selectedStart = selectedStart.startOf("day");
    selectedEnd = selectedEnd.endOf("day");

    // Save first picked for maxRange calculation
    if (!firstPicked) setFirstPicked(selectedStart);

    // Check overlap with occupied dates
    const isOverlapping = occupiedDates.some(({ start, end }) => {
      const startDate = dayjs(start);
      const endDate = dayjs(end);
      return (
        selectedStart.isBetween(startDate, endDate, null, "[]") ||
        selectedEnd.isBetween(startDate, endDate, null, "[]") ||
        startDate.isBetween(selectedStart, selectedEnd, null, "[]") ||
        endDate.isBetween(selectedStart, selectedEnd, null, "[]")
      );
    });

    if (isOverlapping) {
      setWarningMessage("Overlapped invalid date");
      form?.setFieldsValue({ [keyName]: undefined });
      setFirstPicked(null);
      return;
    } else {
      setWarningMessage("");
    }

    onChange({
      start: selectedStart.toISOString(),
      end: selectedEnd.toISOString(),
    });
  };

  const isDateOccupied = (current) =>
    occupiedDates.some(({ start, end }) => current.isBetween(dayjs(start), dayjs(end), null, "[]"));

  const disableDate = (current) => {
    if (!current) return false;

    // Disabled outside min/max limits or occupied
    if (
      (minDate && current.isBefore(minDate, "day")) ||
      (maxDate && current.isAfter(maxDate, "day")) ||
      isDateOccupied(current)
    ) {
      return true;
    }

    // Disabled outside maxRangeInDays from first picked
    if (firstPicked && maxRangeInDays) {
      const minRange = firstPicked.subtract(maxRangeInDays, "day");
      const maxRange = firstPicked.add(maxRangeInDays, "day");
      if (!current.isBetween(minRange, maxRange, null, "[]")) return true;
    }

    return false;
  };

  useEffect(() => {
    if (data?.start && data?.end) {
      const start = dayjs(data.start);
      setFirstPicked(start);
      form?.setFieldsValue({ [keyName]: [start, dayjs(data.end)] });
    } else {
      form?.setFieldsValue({ [keyName]: undefined });
      setFirstPicked(null);
    }
  }, [data, form, keyName]);

  return (
    <Form form={form}>
      <Form.Item name={keyName} rules={rules}>
        <RangePicker
          size="large"
          style={style}
          allowClear={allowClear}
          value={data ? [dayjs(data.start), dayjs(data.end)] : undefined}
          onChange={handlePeriodChange} // Fires when both dates selected
          onCalendarChange={(dates) => {
            if (dates && dates[0]) {
              setFirstPicked(dates[0].startOf("day")); // Track first picked immediately
            } else {
              setFirstPicked(null); // Reset if cleared
            }
          }}
          disabledDate={disableDate}
        />
      </Form.Item>
    </Form>
  );
};

export default PeriodPicker;
